/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useState} from 'react';
import style from '../style/Content.module.less';
import {
  Button,
  Input,
  Pagination,
  Select,
  Table,
  TabPaneProps,
  Tabs,
  Radio,
  RadioChangeEvent,
  ConfigProvider
} from 'antd';
import { cloneDeep } from 'lodash';
import { usePageState } from '@/hooks/hooks';
import IconFont from '@/common/IconFont';


export interface SearchSelectOption {
  label: string;
  value: string | number;
}

interface otherProps {
  headProp?: React.ReactNode;
  tabRight?: React.ReactNode[];
  emptyProp?: React.ReactNode[];
  totalSpan?: React.ReactNode;
}

export interface SearchProps {
  type: 'radio' | 'input' | 'select' | 'none'; // none的时候不作为筛选条件
  field: string;
  placeholder?: string;
  width?: number | 'unset';
  defaultValue?: any;
  options?: SearchSelectOption[] | ((e?: any) => Promise<SearchSelectOption[]>);
  onChange?: (value: any) => void;
  trigger?: string;
  maxLength?: number;
}
export type Ref = {
  search: () => void;
  getState: () => ReturnType<typeof usePageState<any, any>>;
  resetTableColumn: (columns: any) => void;
  resetProp: (prop: otherProps) => void;
  resetBtns: (node: React.ReactNode[]) => void;
  setListEmpty: (data: boolean) => void;
}
interface ContentHeaderTab {
  label: string;
  value: string;
}

interface ContentHeader {
  type: ('card' | 'title' | 'tab')[];
  tabs?: ContentHeaderTab[],
  defaultTab?: string;
}

interface ContentTableProps {
  columns: any;
  actionBtns?: React.ReactNode[];
  totalSpan?: boolean;
  rowSelection?: any;
  footerBtns?: React.ReactNode[];
  rowKey?: any;
}
export interface ContentProps {
  header?: ContentHeader | null;
  searchProps?: SearchProps[] | null;
  title?: string;
  initFn?: InitFn;
  tableProps?: ContentTableProps;
  children?: React.ReactNode[];
  ref?: any;
  hideSearchBtn?: boolean;
  propWrapper?: otherProps;
  subTitle?: React.ReactNode[] | React.ReactNode;
  showIndex?: boolean;
  isNotPage?: boolean;
}

export const Item: React.FC<TabPaneProps> = props => {
  return <Tabs.TabPane {...props}>{props.children}</Tabs.TabPane>;
};

const Content: React.FC<ContentProps> = forwardRef((p, ref) => {
  const state = usePageState<any, any>();
  const [props, setProps] = useState(cloneDeep(p));
  const [column, setColumn] = useState(props.tableProps?.columns || []);
  const [otherProp, setOtherProp] = useState(props.propWrapper);
  const [actionBtns, setActionBtns] = useState(props.tableProps?.actionBtns);
  const [empty, setEmpty] = useState(false);

  const defaultParams = props.searchProps?.reduce((a, c) => {
    return { ...a, [c.field]: c.defaultValue };
  }, {} as Record<string, any>);

  const loadFn = props.isNotPage ? state.loadTableWithoutPage(props.initFn as InitFn) :state.loadTableWithPage(props.initFn as InitFn);


  const search = () => {
    loadFn(state.params);
  };

  const resetTableColumn = (col: any) => {
    setColumn(col);
  };

  const resetProp = (pro: otherProps) => {
    setOtherProp(pro);
  };

  useEffect(() => {
    setOtherProp(p.propWrapper);
  }, [p.propWrapper]);

  const resetBtns = (col: React.ReactNode[]) => {
    setActionBtns(col);
  };

  const setListEmpty = (data: boolean) => {
    setEmpty(data);
  };

  useImperativeHandle(ref, () => {
    return {
      search,
      getState: () => state,
      resetTableColumn,
      resetProp,
      resetBtns,
      setListEmpty
    };
  });

  // 初始化
  const initPage = () => {
    const newParams = { ...state.params, ...defaultParams };
    state.setParams(newParams);
    loadFn(newParams);
  };

  const reset = () => {
    initPage();
  };

  // 分页变化
  const pageChange = (pageNum: number, pageSize: number) => {
    const newParams = {
      ...state.params,
      pageNum, pageSize
    };
    loadFn(newParams);
    state.setParams(newParams);
  };

  const radioChange = (e: RadioChangeEvent, field: string) => {
    const newParams = {
      ...state.params,
      [field]: e.target.value,
      pageNum: 1
    };
    loadFn(newParams);
    state.setParams(newParams);
  };

  useEffect(() => {
    if (!props.searchProps?.find(i => i.type.includes('tab'))) {
      initPage();
    }
  }, []);

  const trigger = async (e: string, fieldName: string) => {
    const searchProps = cloneDeep(p.searchProps) as SearchProps[];
    const s = searchProps.find(i => i.field === fieldName);
    if (s && typeof s.options === 'function') {
      state.setParams((oldParams: Record<string, any>) => {
        return {
          ...oldParams,
          [fieldName]: undefined
        };
      });
      s.options = await s.options(e);
      const newProps = {...props, searchProps};
      setProps(() => newProps);
    }
  };

  const setParamsValue = (fieldName: string, value: any) => {
    const newParams = { ...state.params, pageNum: 1, [fieldName]: value };
    state.setParams(newParams);
    if (props.hideSearchBtn) {
      loadFn(newParams);
    }
  };

  useEffect(() => {
    props.searchProps?.forEach(async i => {
      if (typeof i.options === 'function') {
        const data = await i.options();
        i.options = data;
        setProps({
          ...props,
          searchProps: props.searchProps
        });
      }
    });
  }, [p.searchProps]);

  const listIndex = useMemo(() => {
    if (props.showIndex) return state.list.map((item, index) => ({
      ...item,
      index: ((state.params.pageNum - 1) * state.params.pageSize + index + 1)
    }));
    return state.list;
  }, [state.list, props.showIndex, state.params.pageNum]);

  const columnsIndex = useMemo(() => {
    let c = [...column];
    if (props.showIndex) {
      c.unshift({ title: '序号', dataIndex: 'index', width: 80, key: 'index' });
    }
    return c;
  }, [column, props.showIndex]);

  const renderEmpty = (() => <div className={style.tableEmpty}>
    <img src={require('@/assets/imgs/table-empty.png')} alt=""></img>
    <div>暂无记录</div>
    {otherProp?.emptyProp && [...otherProp.emptyProp]}
  </div>);

  return (
    <div className={`${style.content} ${props.header ? '' : style.noHeader}`}>
      <div className="header">
        {
          // card header
          props.header?.type.includes('card') && (() => (
            <div className={style.cardHeader}>
              <span><span className={style.title}>{props.title}</span>{props.subTitle}</span>
              <span style={{flex: 1}}>{otherProp?.headProp}</span>
            </div>
          ))()
        }
        {
          // title header
          props.header?.type.includes('title') && (() => (
            <div className={style.titleHeader}>
              <span>{props.title}</span>
            </div>
          ))()
        }
      </div>
      {
        // 如果是tab类型的 就不继续渲染了 如果不是 就是继续
        props.header?.type.includes('tab') ? (() => (
          <div className={style.tabHeader}>
            <Tabs destroyInactiveTabPane defaultActiveKey={props.header?.defaultTab} className={style.tab}>
              {
                props.children?.map((i, index) => {
                  const ts = props.header?.tabs as ContentHeaderTab[];
                  const tab = ts[index]?.label;
                  const value = ts[index]?.value;
                  return <Tabs.TabPane tab={tab} key={value}>{i}</Tabs.TabPane>;
                })
              }
            </Tabs>
            {otherProp?.tabRight && [...otherProp.tabRight]}
          </div>
        ))() : (() => (
          <>
            <div className={`${style.search} ${props.searchProps === null || (props.searchProps && props.searchProps.filter(el => el.type !== 'none').length === 0 && (!actionBtns || actionBtns.length === 0)) ? style.null : ''}`}>
              <div>
                {
                  props.searchProps?.map(i => {
                    switch (i.type) {
                    case 'input':
                      return <Input
                        placeholder={i.placeholder}
                        key={i.field}
                        value={state.params[i.field]}
                        onChange={e => {
                          setParamsValue(i.field, e.target.value);
                        }}
                        style={{
                          width: (i.width || 110) + 'px'
                        }}
                        maxLength={i.maxLength || 30}
                        suffix={
                          <IconFont type="icon-sousuo"></IconFont>
                        }
                      />;
                    case 'select':
                      return <Select
                        key={i.field}
                        placeholder={i.placeholder}
                        value={state.params[i.field]}
                        onChange={e => {
                          setParamsValue(i.field, e);
                          i.onChange && i.onChange(e);
                          i.trigger && trigger(e as string, i.trigger);
                        }}
                        style={{
                          width: (i.width || 110) + 'px'
                        }}
                        listHeight={400}
                        allowClear
                      >
                        {
                          typeof i.options === 'object' && i.options?.map(j => <Select.Option key={j.value}>{j.label}</Select.Option>)
                        }
                      </Select>;
                    case 'radio':
                      return <Radio.Group
                        defaultValue={i.defaultValue}
                        style={{
                          width: typeof i.width === 'number' ? (i.width || 110) + 'px' : i.width
                        }}
                        onChange={e => {
                          radioChange(e, i.field);
                          i.onChange && i.onChange(e.target.value);
                        }}
                      >
                        {
                          typeof i.options === 'object' && i.options?.map(j =>
                            <Radio.Button value={j.value} key={j.value}>{j.label}</Radio.Button>
                          )
                        }
                      </Radio.Group>;
                    default:
                      return <></>;
                    }
                  })
                }
                {
                  Boolean(props.searchProps && !props.hideSearchBtn) && <>
                    <Button onClick={reset}>重置</Button>
                    <Button type="primary" onClick={search}>搜索</Button>
                  </>
                }
                {
                  !empty && props.tableProps?.totalSpan && <span className={style.headTitleCount}>共<label>{state.total}</label>人</span>
                }
                {otherProp?.totalSpan}

              </div>
              <div className={style.actionBtns}>
                {
                  actionBtns && [...actionBtns]
                }
              </div>
            </div>
            <div className={`${style.tableEmpty} ${!empty ? style.hide : ''}`}>
              <img src={require('@/assets/imgs/table-empty.png')} alt=""></img>
              <div>暂无记录</div>
              {otherProp?.emptyProp && [...otherProp.emptyProp]}
            </div>
            <div className={`${style.table} ${empty ? style.hide: ''}`}>
              <ConfigProvider renderEmpty={renderEmpty}>
                <Table
                  dataSource={empty ? [] : listIndex}
                  columns={columnsIndex}
                  loading={state.loading}
                  pagination={false}
                  rowKey={p.tableProps?.rowKey || ((r: any) => r.id)}
                  rowSelection={p.tableProps?.rowSelection}
                />
              </ConfigProvider>
              <div className={`${style.tableFooter} ${empty || (!p.tableProps?.footerBtns?.length && (state.total === 0 || props.isNotPage)) ? style.hide : ''}`}>
                <div className={style.footerBtns}>
                  {p.tableProps?.footerBtns && [...p.tableProps.footerBtns]}
                </div>
                <div className={`${style.pagination} ${state.total === 0 || props.isNotPage ? style.hide : ''}`}>
                  <div>共<span>{state.total}</span>条数据</div>
                  <Pagination
                    showQuickJumper
                    showSizeChanger
                    pageSizeOptions={[10, 20, 50]}
                    total={state.total}
                    onChange={pageChange}
                    current={state.params.pageNum}
                    pageSize={state.params.pageSize}
                  />
                </div>
              </div>
            </div>
          </>
        ))()
      }
    </div>
  );
});

export default Content;
